import { supabase } from "@/integrations/supabase/client";

/**
 * Structured logger for failed add-to-cart events.
 * Fire-and-forget: never throws, never awaits UI. Used to build the aggregated
 * failure page (`/admin/add-to-cart-failures`) so we can spot regressions fast.
 */
export type AddToCartFailureReason =
  | "missing_variant"
  | "empty_variant_id"
  | "invalid_variant_gid"
  | "non_numeric_variant_id"
  | "shopify_create_cart_failed"
  | "shopify_add_line_failed"
  | "shopify_update_line_failed"
  | "cart_not_found"
  | "missing_line_id"
  | "insufficient_stock"
  | "unexpected_error";

export interface AddToCartFailurePayload {
  reason: AddToCartFailureReason;
  message?: string | null;
  productId?: string | null;
  productHandle?: string | null;
  productTitle?: string | null;
  variantId?: string | null;
  variantTitle?: string | null;
  quantity?: number | null;
  source?: string | null; // e.g. "product-card", "cart-cross-sell", "cart-store"
  extra?: Record<string, unknown>;
}

const SESSION_KEY = "atcf_sid";

function getSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `sid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

export function logAddToCartFailure(payload: AddToCartFailurePayload): void {
  try {
    const row = {
      reason: payload.reason,
      message: payload.message ?? null,
      product_id: payload.productId ?? null,
      product_handle: payload.productHandle ?? null,
      product_title: payload.productTitle ?? null,
      variant_id: payload.variantId ?? null,
      variant_title: payload.variantTitle ?? null,
      quantity: payload.quantity ?? null,
      source: payload.source ?? null,
      route: typeof window !== "undefined" ? window.location.pathname : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      session_id: getSessionId(),
      extra: payload.extra ?? {},
    };
    void supabase
      .from("add_to_cart_failure_log")
      .insert([row as never])
      .then(({ error }) => {
        if (error && import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn("[atcf] insert failed:", error.message);
        }
      });
  } catch {
    // never let logging break the shopping flow
  }
}
